import './bulk-messages-patient-list-item.scss';

import { kebabCase } from 'lodash';
import { useCallback, useMemo } from 'react';

import { BulkMessagePatientsModalFilter } from '../../../../data/data/enums';
import {
  BulkCampaignType,
  BulkPatientItemDataFragment,
} from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../../data/utils/make-key-down-handler';
import { useNavigateWithHealthProvider } from '../../../../data/utils/router.utils';
import {
  ContextMenuHorizontalPosition,
  ContextMenuVerticalPosition,
  useContextMenu,
} from '../../../../data/utils/use-context-menu';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import {
  ContextMenu,
  ContextMenuItemIcon,
} from '../../../../elements/context-menu/context-menu';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Tag } from '../../../../elements/tag/tag';

type BulkMessagesPatientListItemProps = {
  patientItem: BulkPatientItemDataFragment;
  type?: BulkCampaignType | null;
  active?: boolean | null;
  availableSlotsCount?: number | null;
  onClick?: (value: BulkPatientItemDataFragment) => void;
  onDelete?: (value: BulkPatientItemDataFragment) => void;
  onResend?: (value: BulkPatientItemDataFragment) => void;
};

export function BulkMessagesPatientListItem({
  patientItem,
  type,
  active,
  availableSlotsCount,
  onClick,
  onDelete,
  onResend,
}: BulkMessagesPatientListItemProps) {
  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(patientItem),
        onKeyDown: makeKeyDownHandler(() => onClick(patientItem)),
      };
    }

    return {};
  }, [patientItem, onClick]);

  const { getFullName, getBirthDate } = useFormatters();

  const status = useMemo(() => {
    if (!patientItem.sent) return BulkMessagePatientsModalFilter.NOT_SENT;
    if (patientItem.declined === true)
      return BulkMessagePatientsModalFilter.DECLINED;
    if (patientItem.appointment?.id)
      return BulkMessagePatientsModalFilter.CONFIRMED;
    if (type === BulkCampaignType.Text && patientItem.sent) {
      return BulkMessagePatientsModalFilter.SENT;
    }
    return BulkMessagePatientsModalFilter.PENDING;
  }, [
    patientItem.sent,
    patientItem.declined,
    patientItem.appointment?.id,
    type,
  ]);

  const translation = useMemo(() => {
    if (type === BulkCampaignType.BulkInvite) {
      return !patientItem.sent ? 'common.send-invite' : 'common.resend-invite';
    }
    return !patientItem.sent ? 'common.send-message' : 'common.resend-message';
  }, [patientItem.sent, type]);

  const { contextMenuState, onOpenContextMenu } = useContextMenu({
    horizontal: ContextMenuHorizontalPosition.Right,
    vertical: ContextMenuVerticalPosition.Bottom,
  });

  const navigate = useNavigateWithHealthProvider();

  const onHandleAppointmentChat = useCallback(() => {
    if (patientItem.conversationId) {
      navigate(`/conversations/${patientItem.conversationId}`);
    }
  }, [navigate, patientItem.conversationId]);

  const sendDisabled = useMemo(() => {
    if (patientItem.appointment?.id) {
      return true;
    }
    if (type === BulkCampaignType.BulkInvite) {
      if (!availableSlotsCount || availableSlotsCount === 0) {
        return true;
      }
      return false;
    }
    return false;
  }, [patientItem.appointment?.id, type, availableSlotsCount]);

  // todo converstion + remark
  return (
    <div
      {...attributes}
      className={`bulk-messages-patient-list-item ${
        type ? `bulk-messages-patient-list-item--${kebabCase(status)}` : ''
      }`}
    >
      {type && (
        <div className="bulk-messages-patient-list-item__content bulk-messages-patient-list-item__content--status">
          <h3>
            <CustomFormattedMessage id={`bulk-messages.${kebabCase(status)}`} />
          </h3>
        </div>
      )}
      <div className="bulk-messages-patient-list-item__content bulk-messages-patient-list-item__content--patient">
        <h3>{getFullName(patientItem.patient)}</h3>
        <p>{getBirthDate(patientItem.patient)}</p>
      </div>
      <div className="bulk-messages-patient-list-item__tags">
        <Tag type={patientItem.patient.status} />
      </div>

      <div className="bulk-messages-patient-list-item__actions">
        <Button
          disabled={!patientItem.conversationId}
          type={ButtonType.TRANSPARENT}
          icon={ButtonIcon.COMMENTS}
          onClick={onHandleAppointmentChat}
        />
        <Button
          disabled={active === false}
          type={ButtonType.TRANSPARENT}
          icon={ButtonIcon.ELLIPSIS_VERTICAL}
          onClick={onOpenContextMenu}
        />
      </div>
      {contextMenuState && (
        <ContextMenu
          state={contextMenuState}
          items={[
            {
              key: 'send',
              icon: ContextMenuItemIcon.Send,
              title: <CustomFormattedMessage id={translation} />,
              disabled: sendDisabled,
              onClick: () => {
                onResend?.(patientItem);
              },
            },
            {
              key: 'delete',
              icon: ContextMenuItemIcon.Delete,
              title: <CustomFormattedMessage id="common.delete" />,
              disabled: patientItem.sent,
              onClick: () => {
                onDelete?.(patientItem);
              },
            },
          ]}
        />
      )}
    </div>
  );
}
